import React, { useState, useEffect } from 'react';
import {
  Menu, X, ArrowRight, Check, Leaf, Droplets,
  Shield, Recycle, Phone, Mail, MapPin, Send,
  Package, Truck, Award, Play, Image as ImageIcon,
  Video, ChevronLeft, ChevronRight, MessageCircle, ChevronUp, ChevronDown
} from 'lucide-react';

const KRRFibersWebsite = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [activeProduct, setActiveProduct] = useState(null);
  const [activeTab, setActiveTab] = useState('specifications');
  const [galleryFilter, setGalleryFilter] = useState('all');
  const [lightboxMedia, setLightboxMedia] = useState(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  // Call to action handlers
  const handlePhoneCall = () => {
    window.open('tel:+919876543210', '_self');
  };

  const handleEmailContact = () => {
    window.open('mailto:info@krrfibers.com?subject=Product Inquiry&body=Hello, I am interested in your coconut fiber products. Please provide more information.', '_self');
  };

  const handleWhatsAppContact = (product = null) => {
    const message = product
      ? `Hello! I'm interested in your ${product} product. Could you please provide more details about pricing and availability?`
      : `Hello! I'm interested in your coconut fiber products. Could you please provide more information?`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/919876543210?text=${encodedMessage}`, '_blank');
  };

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const products = [
    {
      id: 1,
      name: "Coconut Fiber (Coir)",
      category: "Natural Fiber",
      shortDesc: "Premium quality natural fiber extracted from coconut husk",
      fullDesc: "Coconut fiber, also known as coir, is a natural fiber extracted from the outer husk of coconut. Highly durable and resistant to saltwater, it is widely used for making floor mats, brushes, and as a soil enhancer.",
      image: "/images/products/coconut-fiber-card.svg",
      features: [
        "Highly durable and saltwater resistant",
        "Excellent water retention properties",
        "Superior aeration capabilities",
        "100% biodegradable and eco-friendly",
        "Natural pH balance for soil health"
      ],
      applications: ["Floor mats and brushes", "Soil enhancement", "Gardening and landscaping", "Erosion control", "Acoustic insulation"],
      specifications: {
        "Moisture Content": "< 15%",
        "pH Level": "5.5 - 6.8",
        "Salt Content": "< 0.5 EC",
        "Fiber Length": "6-30 cm",
        "Packaging": "Bales of 50-100 kg"
      }
    },
    {
      id: 2,
      name: "Coconut Pith (Coco Peat)",
      category: "Growing Medium",
      shortDesc: "Premium growing medium for horticulture and hydroponics",
      fullDesc: "Coconut pith (coco peat) is the spongy material found between the hard shell and the outer coat of coconuts. Used widely in horticulture, it boosts water retention, improves aeration, and is biodegradable.",
      image: "/images/products/coconut-pith-card.svg",
      variants: [
        {
          type: "Low EC",
          description: "Thoroughly washed with minimal salt content (EC <0.5mS/cm), ideal for hydroponics and sensitive plants",
          benefits: ["Perfect for hydroponics", "Sensitive plant cultivation", "Controlled nutrient environment", "Optimal pH balance"]
        },
        {
          type: "High EC",
          description: "Higher salt content (EC >1.0mS/cm), suitable for non-sensitive crops and animal bedding",
          benefits: ["Cost-effective solution", "Animal bedding", "Mulching applications", "Soil amendment"]
        }
      ],
      features: [
        "Excellent water retention capacity",
        "Superior aeration properties",
        "100% organic and biodegradable",
        "pH neutral for plant growth",
        "Pest and disease resistant"
      ],
      applications: ["Hydroponics", "Seed germination", "Plant nurseries", "Greenhouse cultivation", "Soil conditioning"],
      specifications: {
        "Water Holding Capacity": "600-700%",
        "pH Level": "5.2 - 6.8",
        "Expansion Ratio": "1:5 to 1:7",
        "Particle Size": "0-20 mm",
        "Packaging": "5kg compressed blocks"
      }
    },
    {
      id: 3,
      name: "Coconut Chips",
      category: "Substrate Amendment",
      shortDesc: "Premium drainage and aeration solution for specialized growing",
      fullDesc: "Coconut chips are made from dried coconut husk pieces. They offer outstanding drainage and aeration when mixed into soils, making them perfect for orchids, hydroponics, and improving substrate structure.",
      image: "/images/products/coconut-chips-card.svg",
      features: [
        "Outstanding drainage properties",
        "Excellent air circulation",
        "Long-lasting substrate structure",
        "Natural resilience and durability",
        "Maintains air pockets for root health"
      ],
      applications: ["Orchid cultivation", "Hydroponic systems", "Potting mix amendment", "Terrarium substrates", "Specialty plant growing"],
      specifications: {
        "Chip Size": "6-20 mm",
        "Moisture Content": "< 12%",
        "pH Level": "5.5 - 6.5",
        "Air Porosity": "> 30%",
        "Packaging": "Compressed bales"
      }
    },
    {
      id: 4,
      name: "5Kg Pith Blocks",
      category: "Compressed Growing Medium",
      shortDesc: "Convenient compressed blocks expanding to 75-85 liters",
      fullDesc: "The 5kg pith block is a compressed, eco-friendly coconut peat brick, expanding up to 75-85 liters when hydrated. Extensively used in gardens, nurseries, and greenhouses, it is weed-free and known for excellent water retention and aeration.",
      image: "/images/products/pith-blocks-card.svg",
      variants: [
        {
          type: "Low EC Blocks",
          description: "Well-washed blocks with salt content below 0.5mS/cm, ideal for sensitive crops and hydroponic systems",
          benefits: ["Perfect for hydroponics", "Sensitive plant cultivation", "Pure growth medium", "Balanced nutrition"]
        },
        {
          type: "High EC Blocks",
          description: "Higher mineral content blocks (EC >1.0mS/cm) suited for soil amendment and animal bedding",
          benefits: ["Robust applications", "Soil improvement", "Animal bedding", "Cost-effective"]
        },
        {
          type: "Pith & Chips Mixed",
          description: "Optimal blend combining water-holding capacity of pith with aeration of chips",
          benefits: ["Superior drainage", "Reduced compaction", "Healthier root environment", "Versatile applications"]
        }
      ],
      features: [
        "Compact and easy to transport",
        "Expands 15-17 times when hydrated",
        "Weed-free and pathogen-free",
        "Consistent quality and texture",
        "Environmentally sustainable"
      ],
      applications: ["Home gardening", "Commercial nurseries", "Greenhouse operations", "Hydroponic farming", "Soil amendment"],
      specifications: {
        "Block Weight": "5 kg ± 0.1 kg",
        "Expansion Volume": "75-85 liters",
        "Compression Ratio": "15:1 to 17:1",
        "Moisture Content": "< 20%",
        "Packaging": "Individual wrapped blocks"
      }
    }
  ];

  const galleryItems = [
    {
      id: 1,
      type: 'image',
      category: 'facility',
      title: 'Processing Facility',
      description: 'State-of-the-art coconut fiber processing unit',
      thumbnail: '/images/gallery/facility-processing.svg',
      fullImage: '/images/gallery/facility-processing.svg'
    },
    {
      id: 2,
      type: 'image',
      category: 'product',
      title: 'Premium Coconut Fiber',
      description: 'High-quality coir ready for packaging',
      thumbnail: '/images/gallery/product-coir-fiber.svg',
      fullImage: '/images/gallery/product-coir-fiber.svg'
    },
    {
      id: 3,
      type: 'video',
      category: 'process',
      title: 'Fiber Extraction Process',
      description: 'See how we extract quality coconut fiber from husks',
      thumbnail: '/images/gallery/video-extraction-process.svg',
      videoUrl: '#'
    },
    {
      id: 4,
      type: 'image',
      category: 'product',
      title: 'Coconut Pith Blocks',
      description: '5kg compressed pith blocks ready for export',
      thumbnail: '/images/gallery/product-pith-blocks.svg',
      fullImage: '/images/gallery/product-pith-blocks.svg'
    },
    {
      id: 5,
      type: 'image',
      category: 'facility',
      title: 'Quality Control Lab',
      description: 'Advanced testing and quality assurance',
      thumbnail: '/images/gallery/facility-quality-lab.svg',
      fullImage: '/images/gallery/facility-quality-lab.svg'
    },
    {
      id: 6,
      type: 'video',
      category: 'process',
      title: 'Automated Packaging Process',
      description: 'Watch our state-of-the-art packaging system in action',
      thumbnail: '/images/gallery/video-packaging-process.svg',
      videoUrl: '#'
    },
    {
      id: 7,
      type: 'image',
      category: 'product',
      title: 'Coconut Chips',
      description: 'Premium coconut chips for orchids and hydroponics',
      thumbnail: '/images/gallery/product-coconut-chips.svg',
      fullImage: '/images/gallery/product-coconut-chips.svg'
    },
    {
      id: 8,
      type: 'image',
      category: 'facility',
      title: 'Warehouse & Storage',
      description: 'Climate-controlled storage facilities',
      thumbnail: '/images/gallery/facility-warehouse.svg',
      fullImage: '/images/gallery/facility-warehouse.svg'
    },
    {
      id: 9,
      type: 'image',
      category: 'team',
      title: 'Our Expert Team',
      description: 'Dedicated professionals ensuring quality and excellence',
      thumbnail: '/images/gallery/team-photo.svg',
      fullImage: '/images/gallery/team-photo.svg'
    }
  ];

  const filteredGallery = galleryFilter === 'all'
    ? galleryItems
    : galleryItems.filter(item => item.category === galleryFilter || item.type === galleryFilter);

  const benefits = [
    {
      icon: <Leaf className="w-8 h-8" />,
      title: "100% Natural & Organic",
      description: "All our products are derived from natural coconut waste, making them completely organic and eco-friendly.",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: <Droplets className="w-8 h-8" />,
      title: "Superior Water Retention",
      description: "Excellent water holding capacity while maintaining proper drainage for optimal plant growth.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Pest & Disease Resistant",
      description: "Natural resistance to pests and diseases, providing a clean growing environment for plants.",
      color: "from-purple-500 to-violet-500"
    },
    {
      icon: <Recycle className="w-8 h-8" />,
      title: "Sustainable & Renewable",
      description: "Made from renewable coconut waste, contributing to a circular economy and environmental sustainability.",
      color: "from-orange-500 to-amber-500"
    }
  ];

  const ProductCard = ({ product }) => (
    <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
      <div className="aspect-video overflow-hidden">
        <img 
          src={product.image} 
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
      </div>
      
      <div className="p-6">
        <div className="mb-3">
          <span className="text-sm font-medium text-green-600 uppercase tracking-wide">{product.category}</span>
        </div>
        
        <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-green-600 transition-colors">
          {product.name}
        </h3>
        
        <p className="text-gray-600 mb-4">
          {product.shortDesc}
        </p>
        
        <div className="flex flex-wrap gap-2 mb-4">
          {product.features.slice(0, 3).map((feature, index) => (
            <span key={index} className="flex items-center gap-1 text-sm text-green-700 bg-green-50 px-2 py-1 rounded-full">
              <Check size={12} />
              {feature.split(' ').slice(0, 2).join(' ')}
            </span>
          ))}
        </div>

        <button 
          onClick={() => setActiveProduct(product)}
          className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
        >
          Learn More
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );

  const MediaLightbox = ({ media, onClose, onNext, onPrev, currentIndex, totalItems }) => {
    if (!media) return null;

    return (
      <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-lg transition-colors z-10"
        >
          <X size={32} />
        </button>

        {onPrev && (
          <button
            onClick={onPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <ChevronLeft size={32} />
          </button>
        )}

        {onNext && (
          <button
            onClick={onNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <ChevronRight size={32} />
          </button>
        )}

        <div className="max-w-6xl w-full">
          {media.type === 'image' ? (
            <img
              src={media.fullImage}
              alt={media.title}
              className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
            />
          ) : (
            <div className="aspect-video bg-gray-900 rounded-lg flex items-center justify-center">
              <div className="text-center text-white">
                <Video size={64} className="mx-auto mb-4 opacity-50" />
                <p className="text-lg mb-2">{media.title}</p>
                <p className="text-sm text-gray-400">Video playback would be implemented here</p>
              </div>
            </div>
          )}

          <div className="mt-4 text-white text-center">
            <h3 className="text-xl font-bold mb-2">{media.title}</h3>
            <p className="text-gray-300">{media.description}</p>
            {totalItems > 1 && (
              <p className="mt-2 text-sm text-gray-400">
                {currentIndex + 1} / {totalItems}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const ProductModal = ({ product, onClose }) => {
    if (!product) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">{product.name}</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X size={24} />
            </button>
          </div>

          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="w-full aspect-video object-cover rounded-xl"
                />
              </div>
              <div>
                <span className="inline-block px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-4">
                  {product.category}
                </span>
                <p className="text-gray-600 text-lg leading-relaxed mb-6">
                  {product.fullDesc}
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {product.features.slice(0, 4).map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <Check size={16} className="text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Variants Section */}
            {product.variants && (
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Product Variants</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {product.variants.map((variant, index) => (
                    <div key={index} className="p-4 border border-gray-200 rounded-xl">
                      <h4 className="font-semibold text-gray-800 mb-2">{variant.type}</h4>
                      <p className="text-gray-600 text-sm mb-3">{variant.description}</p>
                      <div className="space-y-1">
                        {variant.benefits.map((benefit, bIndex) => (
                          <div key={bIndex} className="flex items-center gap-2">
                            <div className="w-1 h-1 bg-green-500 rounded-full"></div>
                            <span className="text-xs text-gray-600">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
              <div className="flex space-x-8">
                {['specifications', 'applications'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab
                        ? 'border-green-500 text-green-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'specifications' && (
              <div className="grid md:grid-cols-2 gap-4">
                {Object.entries(product.specifications).map(([key, value]) => (
                  <div key={key} className="p-3 bg-gray-50 rounded-lg">
                    <div className="font-medium text-gray-800">{key}</div>
                    <div className="text-gray-600">{value}</div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'applications' && (
              <div className="grid md:grid-cols-2 gap-4">
                {product.applications.map((application, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-gray-700">{application}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-8 p-6 bg-green-50 rounded-xl">
              <h4 className="font-semibold text-gray-800 mb-2">Interested in this product?</h4>
              <p className="text-gray-600 mb-4">Contact us for pricing, bulk orders, and custom specifications.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button 
                  onClick={handlePhoneCall}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Phone size={16} />
                  Call Now
                </button>
                <button 
                  onClick={handleEmailContact}
                  className="px-6 py-3 border border-green-600 text-green-600 rounded-lg hover:bg-green-50 transition-colors flex items-center justify-center gap-2"
                >
                  <Mail size={16} />
                  Send Inquiry
                </button>
                <button 
                  onClick={() => handleWhatsAppContact(product.name)}
                  className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                >
                  <MessageCircle size={16} />
                  WhatsApp
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-40 transition-all duration-300 ${scrollY > 50 ? 'bg-white/95 backdrop-blur-lg shadow-sm border-b border-gray-100' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">K</span>
              </div>
              <div>
                <div className="font-bold text-xl text-gray-800">KRR Fibers</div>
                <div className="text-xs text-gray-600">Natural Coconut Products</div>
              </div>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <a href="#products" className="text-gray-600 hover:text-green-600 transition-colors">Products</a>
              <a href="#gallery" className="text-gray-600 hover:text-green-600 transition-colors">Gallery</a>
              <a href="#about" className="text-gray-600 hover:text-green-600 transition-colors">About</a>
              <a href="#benefits" className="text-gray-600 hover:text-green-600 transition-colors">Benefits</a>
              <a href="#contact" className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                Contact Us
              </a>
            </div>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-lg border-t border-gray-100">
            <div className="px-4 py-4 space-y-4">
              <a href="#products" className="block text-gray-600 hover:text-green-600">Products</a>
              <a href="#gallery" className="block text-gray-600 hover:text-green-600">Gallery</a>
              <a href="#about" className="block text-gray-600 hover:text-green-600">About</a>
              <a href="#benefits" className="block text-gray-600 hover:text-green-600">Benefits</a>
              <a href="#contact" className="block text-gray-600 hover:text-green-600">Contact</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-green-50 via-white to-emerald-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-6">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-4">
                  <Leaf size={16} />
                  Premium Natural Products
                </span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-gray-800 mb-6 leading-tight">
                Premium
                <span className="block bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                  Coconut Fiber
                </span>
                Products
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Sustainable, high-quality coconut coir products for agriculture, horticulture, and hydroponics. From premium coconut fiber to specialized growing mediums, we deliver excellence in every product.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <a href="#products" className="px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all transform hover:scale-105 flex items-center justify-center gap-2">
                  <Package size={20} />
                  View Products
                </a>
                <button 
                  onClick={handleWhatsAppContact}
                  className="px-8 py-4 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all transform hover:scale-105 flex items-center justify-center gap-2"
                >
                  <MessageCircle size={20} />
                  WhatsApp
                </button>
              </div>

              <div className="grid grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">100%</div>
                  <div className="text-sm text-gray-600">Natural & Organic</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">ISO</div>
                  <div className="text-sm text-gray-600">Certified Quality</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">Eco</div>
                  <div className="text-sm text-gray-600">Friendly & Sustainable</div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="aspect-square bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl flex items-center justify-center text-white font-semibold text-lg">
                    Coconut Fiber
                  </div>
                  <div className="aspect-video bg-gradient-to-br from-blue-400 to-cyan-500 rounded-2xl flex items-center justify-center text-white font-semibold">
                    Coconut Pith
                  </div>
                </div>
                <div className="space-y-4 mt-8">
                  <div className="aspect-video bg-gradient-to-br from-orange-400 to-red-500 rounded-2xl flex items-center justify-center text-white font-semibold">
                    Coconut Chips
                  </div>
                  <div className="aspect-square bg-gradient-to-br from-purple-400 to-violet-500 rounded-2xl flex items-center justify-center text-white font-semibold text-lg">
                    5Kg Blocks
                  </div>
                </div>
              </div>
              
              <div className="absolute -inset-4 bg-gradient-to-r from-green-400/20 to-emerald-400/20 rounded-3xl -z-10 blur-xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Premium Products</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover our comprehensive range of coconut-based products, each carefully processed to meet the highest quality standards for various applications.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Media Gallery</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our facilities, products, and processes through our comprehensive photo and video gallery.
            </p>
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <button
              onClick={() => setGalleryFilter('all')}
              className={`px-6 py-2 rounded-full transition-all ${
                galleryFilter === 'all'
                  ? 'bg-green-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              All Media
            </button>
            <button
              onClick={() => setGalleryFilter('image')}
              className={`px-6 py-2 rounded-full transition-all flex items-center gap-2 ${
                galleryFilter === 'image'
                  ? 'bg-green-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ImageIcon size={16} />
              Photos
            </button>
            <button
              onClick={() => setGalleryFilter('video')}
              className={`px-6 py-2 rounded-full transition-all flex items-center gap-2 ${
                galleryFilter === 'video'
                  ? 'bg-green-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Video size={16} />
              Videos
            </button>
            <button
              onClick={() => setGalleryFilter('facility')}
              className={`px-6 py-2 rounded-full transition-all ${
                galleryFilter === 'facility'
                  ? 'bg-green-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              Facilities
            </button>
            <button
              onClick={() => setGalleryFilter('product')}
              className={`px-6 py-2 rounded-full transition-all ${
                galleryFilter === 'product'
                  ? 'bg-green-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              Products
            </button>
            <button
              onClick={() => setGalleryFilter('process')}
              className={`px-6 py-2 rounded-full transition-all ${
                galleryFilter === 'process'
                  ? 'bg-green-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              Process
            </button>
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredGallery.map((item, index) => (
              <div
                key={item.id}
                onClick={() => {
                  setLightboxMedia(item);
                  setCurrentMediaIndex(filteredGallery.findIndex(i => i.id === item.id));
                }}
                className="group relative aspect-video overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
              >
                <img
                  src={item.thumbnail}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <h3 className="font-bold text-lg mb-1">{item.title}</h3>
                    <p className="text-sm text-gray-200">{item.description}</p>
                  </div>
                </div>

                {/* Media Type Badge */}
                <div className="absolute top-3 right-3 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full flex items-center gap-2">
                  {item.type === 'video' ? (
                    <>
                      <Play size={14} className="text-green-600" />
                      <span className="text-xs font-medium text-gray-700">Video</span>
                    </>
                  ) : (
                    <>
                      <ImageIcon size={14} className="text-green-600" />
                      <span className="text-xs font-medium text-gray-700">Photo</span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {filteredGallery.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No media found for this filter.</p>
            </div>
          )}
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Why Choose KRR Fibers?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our commitment to quality and sustainability makes us the preferred choice for coconut fiber products worldwide.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="group p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <div className={`inline-flex p-3 rounded-xl bg-gradient-to-r ${benefit.color} text-white mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  {benefit.icon}
                </div>
                <h3 className="font-bold text-gray-800 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Stats */}
      <section className="py-20 bg-green-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 text-center text-white">
            <div>
              <div className="text-4xl font-bold mb-2">1000+</div>
              <div className="text-green-100">Happy Customers</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">100%</div>
              <div className="text-green-100">Customer Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">
              Get answers to common questions about our coconut fiber products and services.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: "What is the difference between coconut fiber and coconut pith?",
                answer: "Coconut fiber (coir) is the long, stringy material extracted from coconut husks, ideal for erosion control and manufacturing. Coconut pith (coco peat) is the spongy material between the shell and outer coat, perfect for growing mediums and soil amendment."
              },
              {
                question: "What does EC level mean and why is it important?",
                answer: "EC (Electrical Conductivity) measures salt content in our products. Low EC (<0.5mS/cm) is ideal for hydroponics and sensitive plants, while High EC (>1.0mS/cm) is suitable for soil amendment and animal bedding applications."
              },
              {
                question: "How much does a 5kg pith block expand when hydrated?",
                answer: "Our 5kg compressed pith blocks expand to approximately 75-85 liters when fully hydrated with water, providing an expansion ratio of 15:1 to 17:1."
              },
              {
                question: "Are your products suitable for organic farming?",
                answer: "Yes, all our coconut fiber products are 100% natural, organic, and biodegradable. They are free from chemicals and additives, making them perfect for organic farming and sustainable agriculture practices."
              },
              {
                question: "What packaging options do you offer for bulk orders?",
                answer: "We offer flexible packaging options including compressed bales (50-100kg), individual wrapped 5kg blocks, and custom packaging based on your specific requirements and shipping destinations."
              },
              {
                question: "Do you provide international shipping?",
                answer: "Yes, we export to over 50 countries worldwide. We handle all export documentation and work with reliable shipping partners to ensure safe delivery of your orders."
              },
              {
                question: "How do I determine which product is right for my application?",
                answer: "Our team can help you choose the right product based on your specific needs. Contact us with details about your application, and we'll recommend the most suitable coconut fiber product with appropriate specifications."
              },
              {
                question: "What quality certifications do you have?",
                answer: "We maintain ISO quality certifications and follow international standards for processing and packaging. All products undergo rigorous quality testing to ensure consistency and purity."
              }
            ].map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full p-6 text-left hover:bg-gray-50 transition-colors flex justify-between items-center"
                >
                  <h3 className="text-lg font-semibold text-gray-800 pr-4">{faq.question}</h3>
                  <div className="flex-shrink-0">
                    {openFaqIndex === index ? (
                      <ChevronUp size={20} className="text-green-600" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400" />
                    )}
                  </div>
                </button>
                
                {openFaqIndex === index && (
                  <div className="px-6 pb-6">
                    <div className="pt-2 border-t border-gray-100">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">Have more questions?</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handlePhoneCall}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
              >
                <Phone size={16} />
                Call Us
              </button>
              <button
                onClick={handleWhatsAppContact}
                className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
              >
                <MessageCircle size={16} />
                WhatsApp Chat
              </button>
            </div>
          </div>
        </div>
      </section>
      
      {/* About Section */}
      <section id="about" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">
                About KRR Fibers
              </h2>
              <div className="text-lg text-gray-600 space-y-6 leading-relaxed">
                <p>
                  With over 15 years of experience in the coconut fiber industry, KRR Fibers has established itself as a leading manufacturer and exporter of premium coconut-based products. We specialize in converting coconut waste into valuable, sustainable products for agriculture and horticulture.
                </p>
                <p>
                  Our state-of-the-art processing facilities ensure consistent quality and adherence to international standards. From coconut fiber to specialized growing mediums, every product undergoes rigorous quality control to meet the diverse needs of our global clientele.
                </p>
                <p>
                  Committed to environmental sustainability, we transform agricultural waste into valuable resources, contributing to a circular economy while supporting farmers and promoting eco-friendly practices.
                </p>
              </div>
              
              <div className="mt-8 flex flex-wrap gap-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full">
                  <Award size={16} />
                  ISO Certified
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full">
                  <Truck size={16} />
                  Global Shipping
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-full">
                  <Leaf size={16} />
                  Eco-Friendly
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-green-100 to-emerald-100 rounded-3xl p-8">
                <div className="h-full bg-white rounded-2xl shadow-xl flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                      KRR
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Premium Quality</h3>
                    <p className="text-gray-600">Trusted by customers worldwide for consistent quality and reliability</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Get In Touch</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Ready to discuss your coconut fiber product needs? Contact us for personalized solutions and competitive pricing.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 text-green-600 rounded-lg">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">Phone</h3>
                    <p className="text-gray-600">
                      <a href="tel:+919876543210" className="hover:text-green-600 transition-colors">+91 98765 43210</a>
                    </p>
                    <p className="text-gray-600">
                      <a href="tel:+918765432109" className="hover:text-green-600 transition-colors">+91 87654 32109</a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 text-green-600 rounded-lg">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">Email</h3>
                    <p className="text-gray-600">
                      <a href="mailto:info@krrfibers.com" className="hover:text-green-600 transition-colors">info@krrfibers.com</a>
                    </p>
                    <p className="text-gray-600">
                      <a href="mailto:sales@krrfibers.com" className="hover:text-green-600 transition-colors">sales@krrfibers.com</a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 text-green-600 rounded-lg">
                    <MessageCircle size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">WhatsApp</h3>
                    <p className="text-gray-600">
                      <button 
                        onClick={handleWhatsAppContact}
                        className="hover:text-green-600 transition-colors"
                      >
                        +91 98765 43210
                      </button>
                    </p>
                    <p className="text-gray-500 text-sm">Quick chat support available</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 text-green-600 rounded-lg">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">Address</h3>
                    <p className="text-gray-600">
                      Industrial Estate,<br />
                      Coimbatore, Tamil Nadu, India<br />
                      PIN: 641001
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Send us a message</h3>
              <form 
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const subject = encodeURIComponent(formData.get('subject') || 'Product Inquiry');
                  const message = encodeURIComponent(
                    `Name: ${formData.get('name')}\nEmail: ${formData.get('email')}\n\nMessage:\n${formData.get('message')}`
                  );
                  window.open(`mailto:info@krrfibers.com?subject=${subject}&body=${message}`, '_self');
                }}
                className="space-y-6"
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                    <input 
                      type="text" 
                      name="name"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Your Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input 
                      type="email" 
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                  <input 
                    type="text" 
                    name="subject"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Inquiry about coconut products"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <textarea 
                    rows="4"
                    name="message"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Tell us about your requirements..."
                  ></textarea>
                </div>

                <button 
                  type="submit"
                  className="w-full px-6 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Send size={20} />
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">K</span>
                </div>
                <div>
                  <div className="font-bold text-xl">KRR Fibers</div>
                  <div className="text-sm text-gray-400">Natural Coconut Products</div>
                </div>
              </div>
              <p className="text-gray-400 mb-4 max-w-md">
                Leading manufacturer of premium coconut fiber products, committed to quality, sustainability, and customer satisfaction for over 15 years.
              </p>
              <div className="flex gap-4">
                <button 
                  onClick={handlePhoneCall}
                  className="w-8 h-8 bg-green-600 hover:bg-green-700 rounded-lg flex items-center justify-center text-white transition-colors"
                  title="Call us"
                >
                  📞
                </button>
                <button 
                  onClick={handleEmailContact}
                  className="w-8 h-8 bg-green-600 hover:bg-green-700 rounded-lg flex items-center justify-center text-white transition-colors"
                  title="Email us"
                >
                  📧
                </button>
                <button 
                  onClick={handleWhatsAppContact}
                  className="w-8 h-8 bg-green-500 hover:bg-green-600 rounded-lg flex items-center justify-center text-white transition-colors"
                  title="WhatsApp us"
                >
                  💬
                </button>
                <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center">🌐</div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Products</h3>
              <div className="space-y-2 text-gray-400">
                <div>Coconut Fiber</div>
                <div>Coconut Pith</div>
                <div>Coconut Chips</div>
                <div>5Kg Pith Blocks</div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <div className="space-y-2 text-gray-400">
                <div>About Us</div>
                <div>Quality Standards</div>
                <div>Sustainability</div>
                <div>Contact</div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-8 pt-8">
            <div className="text-center text-gray-400 mb-6">
              <p>© 2025 KRR Fibers. All rights reserved. | Premium Coconut Products for Global Markets</p>
            </div>

            {/* Gowdy's Web Branding */}
            <div className="text-center">
              <p className="text-gray-500 text-sm mb-3">Website Designed & Built by</p>
              <a
                href="https://www.gowdysweb.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block hover:scale-105 transition-transform duration-300"
                aria-label="Visit Gowdy's Web - Custom Website Development"
              >
                <svg
                  viewBox="0 0 380 240"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ width: '190px', height: '120px' }}
                  className="mx-auto"
                >
                  {/* License Plate */}
                  <rect x="20" y="50" width="340" height="140" rx="8" fill="#0a0a0a" stroke="#fff" strokeWidth="6"/>

                  {/* Screws */}
                  <circle cx="40" cy="70" r="7" fill="#666"/>
                  <circle cx="40" cy="70" r="5" fill="#333"/>
                  <line x1="35" y1="70" x2="45" y2="70" stroke="#1a1a1a" strokeWidth="1.5"/>

                  <circle cx="340" cy="70" r="7" fill="#666"/>
                  <circle cx="340" cy="70" r="5" fill="#333"/>
                  <line x1="335" y1="70" x2="345" y2="70" stroke="#1a1a1a" strokeWidth="1.5"/>

                  <circle cx="40" cy="170" r="7" fill="#666"/>
                  <circle cx="40" cy="170" r="5" fill="#333"/>
                  <line x1="35" y1="170" x2="45" y2="170" stroke="#1a1a1a" strokeWidth="1.5"/>

                  <circle cx="340" cy="170" r="7" fill="#666"/>
                  <circle cx="340" cy="170" r="5" fill="#333"/>
                  <line x1="335" y1="170" x2="345" y2="170" stroke="#1a1a1a" strokeWidth="1.5"/>

                  {/* Text */}
                  <text
                    x="190"
                    y="110"
                    textAnchor="middle"
                    style={{
                      fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive",
                      fontSize: '38px',
                      fontWeight: 400,
                      fill: '#fff',
                      letterSpacing: '2px',
                      fontStyle: 'italic'
                    }}
                  >
                    Gowdy's
                  </text>

                  <line x1="70" y1="130" x2="310" y2="130" stroke="#fff" strokeWidth="3"/>

                  <text
                    x="190"
                    y="160"
                    textAnchor="middle"
                    style={{
                      fontFamily: "'Impact', 'Arial Black', sans-serif",
                      fontSize: '18px',
                      fontWeight: 900,
                      fill: '#ff4500',
                      letterSpacing: '6px'
                    }}
                  >
                    WEB
                  </text>
                </svg>
              </a>
              <p className="text-gray-500 text-xs mt-2">Custom Built Digital Machines</p>
            </div>
          </div>
        </div>
      </footer>

      {/* Product Modal */}
      <ProductModal product={activeProduct} onClose={() => setActiveProduct(null)} />

      {/* Media Lightbox */}
      <MediaLightbox
        media={lightboxMedia}
        currentIndex={currentMediaIndex}
        totalItems={filteredGallery.length}
        onClose={() => setLightboxMedia(null)}
        onNext={currentMediaIndex < filteredGallery.length - 1 ? () => {
          const nextIndex = currentMediaIndex + 1;
          setCurrentMediaIndex(nextIndex);
          setLightboxMedia(filteredGallery[nextIndex]);
        } : null}
        onPrev={currentMediaIndex > 0 ? () => {
          const prevIndex = currentMediaIndex - 1;
          setCurrentMediaIndex(prevIndex);
          setLightboxMedia(filteredGallery[prevIndex]);
        } : null}
      />

      {/* Floating WhatsApp Button */}
      <button
        onClick={handleWhatsAppContact}
        className="fixed bottom-6 right-6 w-14 h-14 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-50 flex items-center justify-center group"
        title="Chat with us on WhatsApp"
      >
        <MessageCircle size={24} />
        <div className="absolute right-16 top-1/2 transform -translate-y-1/2 bg-gray-800 text-white px-3 py-2 rounded-lg text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
          Chat with us!
        </div>
      </button>
    </div>
  );
};

export default KRRFibersWebsite;